// src/lib/db.js
// Thin client that calls our Netlify DB proxy function.
// No database credentials ever touch the browser.

// Actions the server authorizes against the caller's VERIFIED identity (the
// JWT), not a self-asserted body field. For these we attach the current
// Supabase access token so the function can resolve who is really calling.
// "Delete All Players" (delete_standalone_round) is creator-only; the server
// denies it outright when no valid token is present, so a hostile hand-crafted
// request with someone else's player_id can no longer wipe a round.
// grab_round transfers round ownership to the caller, so the server must
// authorize on the VERIFIED identity (JWT), not a self-asserted body field.
// get_standalone_scores attaches the token so the server can key the outbound
// friend-privacy gate (F-039) on the VERIFIED viewer — a friend viewing a
// drill-down is gated, while a participant/scorekeeper (who resolves from the
// same token) keeps full visibility. No token → server treats the caller as an
// anonymous non-participant and gates the read (fails closed).
// block_user / unblock_user authorize on the VERIFIED caller (the blocker is
// the JWT identity, never a self-asserted body id), so the token must ride
// along — the server resolves ctx.authPlayerId from it (Guideline 1.2).
// remove_friend (un-friend) deletes the caller's own friendship row; the server
// resolves the caller side of the pair from the JWT (never a body id), so the
// token must ride along — without it authPlayerId is null and the DELETE
// silently no-ops (the same failure mode block_user has without a token).
// get_player_stats returns the caller's OWN personal stats (Stats page tiles);
// the server keys the rollup on the verified JWT identity (never a body id), so
// the token must ride along — no token → server returns an empty shape.
const AUTH_ACTIONS = new Set([
  'delete_standalone_round', 'grab_round', 'get_standalone_scores',
  'block_user', 'unblock_user', 'remove_friend', 'get_player_stats',
  // D-346: remove_guest_seat is owner-gated server-side (round owner resolved
  // from the JWT), so the token must ride along — no token → authPlayerId null
  // → the server denies the removal (fails closed).
  'remove_guest_seat',
  // Rework v2 (auth fix): rename_guest_seat now requires a VERIFIED caller — the
  // server rejects it when no valid JWT resolves an authPlayerId. It is NOT
  // owner-only (a non-owner co-scorer legitimately renames a guest), and the
  // auth_user_id IS NULL guest guard still applies. Attach the token so a real
  // rename carries identity; no token → server denies (fails closed).
  'rename_guest_seat',
  // F-105-T1: tee-mark writes attribute to the VERIFIED caller server-side
  // (set_by = ctx.authPlayerId). The server rejects unauthenticated calls, so
  // the token must ride along — otherwise HoleMap.jsx and ManualHoleMap.jsx
  // writes 401 silently (both call sites swallow errors).
  'set_tee_mark',
])

// Lazily import the Supabase client only when an auth-gated action needs a
// token. A top-level import would instantiate createClient() at module load,
// which throws when VITE_SUPABASE_* env vars are absent (e.g. unit tests that
// merely import this module for the db() helper). Deferring keeps db() usable
// everywhere and only pays the cost on the rare destructive action.
async function authHeaderForAction(action) {
  if (!AUTH_ACTIONS.has(action)) return null
  try {
    const { supabase } = await import('./supabase')
    const { data } = await supabase.auth.getSession()
    const token = data?.session?.access_token
    return token ? `Bearer ${token}` : null
  } catch {
    // No token available — server treats the caller as unauthenticated and
    // denies the owner-gated action, the safe failure direction.
    return null
  }
}

// Set of write actions that must survive a phone-lock mid-flight. iOS
// and Android will abort pending fetches when the tab is backgrounded;
// keepalive keeps them running long enough to reach the server.
// Reads (get_*, list_*, check_*) don't need it — worst case is a re-fetch
// on next mount.
const KEEPALIVE_ACTIONS = new Set([
  'upsert_standalone_score',
  'upsert_active_round',
  'upsert_round_score',
  'delete_active_round',
  'delete_standalone_round',
  'delete_my_standalone_score',
  'find_or_create_player_stub',
  // D-346: mid-round seat mutations must survive a phone-lock mid-flight.
  'rename_guest_seat',
  'remove_guest_seat',
])

export async function db(action, payload = {}) {
  const headers = { 'Content-Type': 'application/json' }
  const authHeader = await authHeaderForAction(action)
  if (authHeader) headers.Authorization = authHeader
  const init = {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, payload }),
  }
  if (KEEPALIVE_ACTIONS.has(action)) init.keepalive = true

  const res = await fetch('/.netlify/functions/db', init)

  const json = await res.json()

  if (!res.ok) {
    throw new Error(json.error || `DB error: ${res.status}`)
  }

  return json // { data: ... }
}

// Narrow beacon helper for the ONE terminal-visibility write path we
// care about — persisting the in-flight scorecard payload to
// active_rounds so a phone-lock mid-save doesn't lose position.
// Deliberately not a generic "call any action" surface: the beacon
// endpoint (/db) is currently unauthenticated, and constraining the
// action set here bounds what a hostile caller could do by hand-crafting
// a beacon payload to nothing an authenticated fetch couldn't already
// do. Rejects payloads without a standalone_round_id + owner_player_id
// so a mis-wired caller can't inadvertently blank the resume cache.
export function sendActiveRoundBeacon(payload) {
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false
    if (!payload?.standalone_round_id || !payload?.owner_player_id) return false
    const blob = new Blob(
      [JSON.stringify({ action: 'upsert_active_round', payload })],
      { type: 'application/json' },
    )
    return navigator.sendBeacon('/.netlify/functions/db', blob)
  } catch { return false }
}
